/**
 * useLocalLocationTracking Hook
 *
 * Reusable, always-on LOCAL device-position watch. Shared core extracted from
 * `useLocationSharing` so both the authenticated map and the public share map
 * can render the viewer's own "you" dot from the same logic — with NO auth,
 * workspace, presence, or broadcast concerns.
 *
 * Responsibilities:
 * - Tracks the geolocation permission state via `geolocationService`
 *   (`watchPermission`, with the watch error callback as a fallback) and writes
 *   it to `map.locationPermission`. NEVER prompts on mount.
 * - On `granted`: starts a continuous `geolocationService.watchPosition` watch
 *   and writes each fix to the local live dot (`map.liveLocation`).
 * - On `denied`: stops the watch and clears the local dot.
 * - Optionally forwards each fix to `options.onFix` so a consumer (e.g.
 *   `useLocationSharing`) can layer broadcast behavior on top WITHOUT owning the
 *   watch lifecycle.
 *
 * @module hooks/useLocalLocationTracking
 */
import { useEffect, useRef } from 'react';

import { geolocationService } from '@/services/geolocation/geolocationService';
import { useGeoMarkupStore } from '@/store';
import {
  GEOLOCATION_HIGH_ACCURACY,
  GEOLOCATION_TIMEOUT_MS,
  GEOLOCATION_MAX_AGE_SHORT_MS,
  PERMISSION_GRANTED,
  PERMISSION_DENIED,
} from '@/constants/geolocation';
import type { MemberLocation } from '@/types';

import { useDeviceHeading } from './useDeviceHeading';

/** Watch options reused for the always-on local position watch. */
const WATCH_OPTIONS = {
  enableHighAccuracy: GEOLOCATION_HIGH_ACCURACY,
  timeout: GEOLOCATION_TIMEOUT_MS,
  maximumAge: GEOLOCATION_MAX_AGE_SHORT_MS,
} as const;

/** Options for the local location tracking core. */
interface UseLocalLocationTrackingOptions {
  /** Invoked with every accepted fix AFTER the local dot is written. */
  onFix?: (location: MemberLocation) => void;
}

/**
 * Starts an always-on local device-position watch when geolocation permission
 * is `granted`, writing the viewer's own dot to the store. Param-less use is
 * purely local (public map); pass `onFix` to layer broadcast behavior on top.
 *
 * @example
 * // Public map — local dot only, no broadcast.
 * useLocalLocationTracking();
 *
 * @example
 * // Authenticated map — broadcast each fix on top of the shared core.
 * useLocalLocationTracking({ onFix: maybeBroadcast });
 */
export function useLocalLocationTracking(
  options?: UseLocalLocationTrackingOptions
): void {
  // Stable ref to the live device heading, updated by `useDeviceHeading` on
  // every orientation tick WITHOUT re-rendering this app-root hook.
  const headingRef = useDeviceHeading();

  // Latest `onFix` callback, read inside the long-lived watch handler so a new
  // callback identity does not tear down the watch.
  const onFixRef = useRef(options?.onFix);
  onFixRef.current = options?.onFix;

  const watchIdRef = useRef<number | null>(null);

  // Always-on watch + permission lifecycle. Runs once. (Effect 1/1)
  useEffect(() => {
    const { setLiveLocation, setLocationPermission } =
      useGeoMarkupStore.getState().map;

    let isActive = true;

    function handleFix(location: {
      lat: number;
      lng: number;
      heading?: number | null;
    }): void {
      // The service normalizes `NaN` (stationary device) to `null`, and the
      // heading ref is `null` until a usable reading arrives — both collapse to
      // `undefined` so no `NaN` ever leaks into the store or callback.
      const fixHeading = location.heading ?? headingRef.current ?? undefined;

      const nextLocation: MemberLocation = {
        lat: location.lat,
        lng: location.lng,
        heading: fixHeading,
      };

      setLiveLocation(nextLocation);
      onFixRef.current?.(nextLocation);
    }

    function handleWatchError(errorCode: string): void {
      if (errorCode === 'PERMISSION_DENIED') {
        applyPermission(PERMISSION_DENIED);
      }
    }

    function startWatch(): void {
      if (watchIdRef.current !== null) {
        return;
      }
      watchIdRef.current = geolocationService.watchPosition(
        handleFix,
        handleWatchError,
        WATCH_OPTIONS
      );
    }

    function stopWatch(): void {
      geolocationService.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    function applyPermission(nextState: PermissionState | null): void {
      if (!isActive) {
        return;
      }
      setLocationPermission(nextState);

      if (nextState === PERMISSION_GRANTED) {
        startWatch();
        return;
      }

      if (nextState === PERMISSION_DENIED) {
        stopWatch();
        // Clears the local dot; consumers reading this null stop reacting.
        setLiveLocation(null);
      }
    }

    // Delivers the INITIAL permission state and every subsequent change. When
    // the Permissions API is unsupported, nothing is delivered — the watch
    // error callback is the fallback path for detecting a denied permission.
    // Does not prompt on mount.
    const unsubscribePermission =
      geolocationService.watchPermission(applyPermission);

    return () => {
      isActive = false;
      stopWatch();
      setLiveLocation(null);
      unsubscribePermission();
    };
    // `headingRef` is the stable ref object from `useDeviceHeading` (identity
    // never changes), so this effect still mounts exactly once.
  }, [headingRef]);
}
